<template>
  <div class="consents-page">
    <PageHeader
      :title="t('rejects.title')"
      :summary="t('rejects.summary')"
    />

    <!-- Content Section with DataTable -->
    <SpmTableAPI
      v-if="apiCall && columns.length > 0"
      ref="childComponentRef"
      :api-call="apiCall"
      :columns="columns"
      :show-reload-button="true"
      data-key="id"
      :initial-sort="{ field: 'created_at_reject', order: 'desc' }"
      :show-export-all-button="findPermission('rejets.export')"
      :exportable="findPermission('rejets.export')"
      :export-config="{ entityType: 'reject' }"
      :enable-grouped-actions="true"
      @export="handleExport"
    >
      <!-- Composite column: fullname (firstname, lastname) -->
      <template #cell-fullname="{ data }">
        <div class="fullname-cell">
          <div class="name-row font-bold">
            <span
              v-if="formatFullName(data.first_name, data.last_name)"
              class="contact-name"
            >
              {{ formatFullName(data.first_name, data.last_name) }}
            </span>
            <span
              v-else
              class="contact-name contact-name--empty"
            >
              {{ t('rejects.noName') }}
            </span>
          </div>
        </div>
      </template>

      <!-- Email column -->
      <template #cell-email="{ data }">
        <span v-if="cleanStr(data.email)">{{ cleanStr(data.email) }}</span>
        <span
          v-else
          class="cell-empty"
        >-</span>
      </template>

      <!-- Sms column -->
      <template #cell-sms="{ data }">
        <div class="text-center w-full flex justify-content-center">
          <span v-if="cleanStr(data.phone)">{{ formatPhoneNumber(data.phone) }}</span>
          <i
            v-else
            v-tooltip.top="t('rejects.noPhone')"
            class="fa fa-phone-slash cell-icon cell-icon--inactive"
          />
        </div>
      </template>

      <!-- Push column -->
      <template #cell-push="{ data }">
        <div class="text-center w-full flex justify-content-center">
          <i
            v-if="cleanStr(data.push)"
            v-tooltip.top="t('rejects.hasPush')"
            class="fa fa-bell cell-icon cell-icon--active"
          />
          <i
            v-else
            v-tooltip.top="t('rejects.noPush')"
            class="fa fa-bell-slash cell-icon cell-icon--inactive"
          />
        </div>
      </template>

      <!-- Channel column -->
      <template #cell-channel="{ data }">
        <SpmBadge
          :label="getChannelLabel(data.channel_reject)"
          :icon="getChannelIcon(data.channel_reject)"
          :colors="getChannelColors(data.channel_reject)"
        />
      </template>

      <!-- Type column -->
      <template #cell-type="{ data }">
        <SpmBadge
          :label="getTypeLabel(data.type_reject)"
          :colors="getTypeColors(data.type_reject)"
        />
      </template>

      <!-- Created at column -->
      <template #cell-created_at="{ data }">
        <SpmDateDisplay
          :value="data.created_at_reject"
          format="datetime"
        />
      </template>
    </SpmTableAPI>
  </div>
</template>

<script lang="ts">
import { useI18n } from 'vue-i18n';
import PageHeader from '@/components/layout/PageHeader.vue';
import SpmTableAPI from '@/components/table-rest-api/SpmTableAPI.vue';
import SpmBadge from '@/components/table-rest-api/SpmBadge.vue';
import SpmDateDisplay from '@/components/table-rest-api/SpmDateDisplay.vue';
import type { SpmColumnConfig, ApiCallConfig, ExportPayload } from '@/components/table-rest-api/types';
import {
  getChannelIcon as getChannelIconFromTokens,
  getChannelTagStyleVars,
} from '@/views/automated-scenarios/utils/styleTokens';
import { NestApiType, nestCall } from '@/composables/nestApi';
import { defineComponent, ref } from 'vue';
import { showToastError, showToastSuccess } from '@/helpers';
import { findPermission } from '@/composables/User';

export default defineComponent({
  name: 'ListsRejects',

  components: {
    SpmTableAPI,
    PageHeader,
    SpmBadge,
    SpmDateDisplay,
  },

  setup() {
    const { t } = useI18n();

    const childComponentRef = ref();

    const setLoading = async (loading: boolean) => {
      if (childComponentRef.value) {
        await childComponentRef.value.setLoading(loading);
      }
    };

    // API configuration
    const apiCall = ref<ApiCallConfig>({
      type_api: NestApiType.CLIENT_API,
      path: '/contact-messages-reject',
      secure: true,
    });

    // Columns configuration
    const columns: SpmColumnConfig[] = [
      {
        id: 'id',
        header: '',
        fields: ['id'],
        hidden: true,
      },
      {
        id: 'fullname',
        header: t('rejects.headers.fullName'),
        fields: [{
          name: 'first_name',
          label: t('rejects.headers.firstname'),
          sortable: true,
          filterable: true,
          filterConfig: {
            type: 'textInput',
          },
        }, {
          name: 'last_name',
          label: t('rejects.headers.lastname'),
          sortable: true,
          filterable: true,
          filterConfig: {
            type: 'textInput',
          },
        }],
      },
      {
        id: 'email',
        header: t('rejects.headers.email'),
        fields: [{
          name: 'email',
          label: t('rejects.headers.email'),
          sortable: true,
          filterable: true,
          filterConfig: {
            type: 'textInput',
          },
        }],
      },
      {
        id: 'sms',
        header: t('rejects.headers.phone'),
        fields: [{
          name: 'phone',
          label: t('rejects.headers.phone'),
          sortable: true,
          filterable: true,
          filterConfig: {
            type: 'textInput',
          },
        }],
      },
      {
        id: 'push',
        header: t('rejects.headers.push'),
        fields: ['push'],
      },
      {
        id: 'channel',
        header: t('rejects.headers.channel'),
        fields: [{
          name: 'channel_reject',
          label: t('rejects.headers.channel'),
          sortable: true,
          filterable: true,
          filterConfig: {
            type: 'singleSelect',
            options: [
              { label: t('rejects.channels.email'), value: 1 },
              { label: t('rejects.channels.sms'), value: 2 },
              { label: t('rejects.channels.whatsapp'), value: 3 },
              { label: t('rejects.channels.push'), value: 4 },
            ],
          },
        }],
      },
      {
        id: 'type',
        header: t('rejects.headers.type'),
        fields: [{
          name: 'type_reject',
          label: t('rejects.headers.type'),
          sortable: true,
          filterable: true,
          filterConfig: {
            type: 'singleSelect',
            options: [
              { label: t('rejects.types.subscribe'), value: 'subscribe' },
              { label: t('rejects.types.unsubscribe'), value: 'unsubscribe' },
              { label: t('rejects.types.spam_compliant'), value: 'spam_compliant' },
              { label: t('rejects.types.hard_bounce'), value: 'hard_bounce' },
              { label: t('rejects.types.soft_bounce'), value: 'soft_bounce' },
            ],
          },
        }],
      },
      {
        id: 'created_at',
        header: t('rejects.headers.createdAt'),
        fields: [{
          name: 'created_at_reject',
          label: t('rejects.headers.createdAt'),
          sortable: true,
          filterable: true,
          filterConfig: {
            type: 'dateRange',
          },
        }],
      },
    ];

    /**
     * Get channel label
     */
    const getChannelLabel = (channel: number): string => {
      const channelMap: Record<number, string> = {
        1: t('rejects.channels.email'),
        2: t('rejects.channels.sms'),
        3: t('rejects.channels.whatsapp'),
        4: t('rejects.channels.push'),
      };
      return channelMap[channel] || '-';
    };

    // Map channel number to channel name for styleTokens
    const channelNumberToName: Record<number, string> = {
      1: 'email',
      2: 'sms',
      3: 'whatsapp',
      4: 'pushnotifications',
    };

    /**
     * Get channel icon using styleTokens
     */
    const getChannelIcon = (channel: number): string => {
      const channelName = channelNumberToName[channel] || '';
      return getChannelIconFromTokens(channelName) || 'fa-solid fa-question';
    };

    /**
     * Get channel colors for SpmBadge using styleTokens
     */
    const getChannelColors = (channel: number): { bg: string; color: string; borderColor?: string } => {
      const channelName = channelNumberToName[channel] || '';
      const vars = getChannelTagStyleVars(channelName);
      return {
        bg: vars['--channel-bg'] || 'var(--surface-200)',
        color: vars['--channel-color'] || 'var(--text-color)',
        borderColor: vars['--channel-border'],
      };
    };

    /**
     * Get type label
     */
    const getTypeLabel = (type: string): string => {
      const normalizedType = type?.toLowerCase() || '';
      const typeMap: Record<string, string> = {
        subscribe: t('rejects.types.subscribe'),
        unsubscribe: t('rejects.types.unsubscribe'),
        spam_complaint: t('rejects.types.spam_compliant'),
        hard_bounce: t('rejects.types.hard_bounce'),
        soft_bounce: t('rejects.types.soft_bounce'),
      };
      return typeMap[normalizedType] || type;
    };

    /**
     * Get type colors for SpmBadge
     */
    const getTypeColors = (type: string): { bg: string; color: string } => {
      const normalizedType = type?.toLowerCase() || '';
      const colorMap: Record<string, { bg: string; color: string }> = {
        subscribe: { bg: 'var(--green-100)', color: 'var(--green-700)' },
        unsubscribe: { bg: 'var(--red-100)', color: 'var(--red-700)' },
        spam_complaint: { bg: 'var(--orange-100)', color: 'var(--orange-700)' },
        hard_bounce: { bg: 'var(--red-100)', color: 'var(--red-700)' },
        soft_bounce: { bg: 'var(--yellow-100)', color: 'var(--yellow-700)' },
      };
      return colorMap[normalizedType] || { bg: 'var(--gray-100)', color: 'var(--gray-700)' };
    };

    // Helper to clean null/undefined strings
    const cleanStr = (str: string | null | undefined): string => {
      if (!str || str === 'null' || str === 'undefined') return '';
      return str;
    };

    // Helper to format full name
    const formatFullName = (firstName: string | null | undefined, lastName: string | null | undefined): string => {
      const first = cleanStr(firstName);
      const last = cleanStr(lastName);
      if (!first && !last) return '';
      return `${first} ${last}`.trim();
    };

    // Method to format phone numbers
    const formatPhoneNumber = (phone: string) => {
      if (!phone || phone === 'null') return '';
      // Remove all non-digit characters except +
      const cleaned = phone.replace(/[^\d+]/g, '');
      // Format with spaces every 2 digits for readability
      if (cleaned.startsWith('+')) {
        const countryCode = cleaned.slice(0, 3);
        const rest = cleaned.slice(3);
        return `${countryCode} ${rest.replace(/(\d{2})(?=\d)/g, '$1 ').trim()}`;
      }
      return cleaned.replace(/(\d{2})(?=\d)/g, '$1 ').trim();
    };

    /**
     * Handle export action from SpmTableAPI
     */
    const handleExport = async (payload: ExportPayload) => {
      try {
        setLoading(true);
        const exportRequest: Record<string, unknown> = {
          entity_type: payload.exportConfig.entityType,
          selection_type: payload.selectAllMode ? 'all' : 'manual',
          selected_ids: payload.selectedIds,
          total_count: payload.totalCount,
          ...payload.exportConfig.additionalParams,
        };

        // Add filters only if present (selectAllMode from selection, not "Export all" button)
        if (payload.filters) {
          exportRequest.filters = payload.filters;
        }

        const response = await nestCall(
          'post',
          NestApiType.CLIENT_API,
          '/export/init',
          {},
          '',
          exportRequest,
          true,
        );

        if (response && !response.error) {
          await showToastSuccess(t('myLists.manage.customers.export.success'));
        } else {
          await showToastError(t('myLists.manage.customers.export.error'));
        }
      } catch (error) {
        console.error('Export error:', error);
        await showToastError(t('myLists.manage.customers.export.error'));
      } finally {
        setLoading(false);
      }
    };

    return {
      t,
      childComponentRef,
      apiCall,
      columns,
      getChannelLabel,
      getChannelIcon,
      getChannelColors,
      getTypeLabel,
      getTypeColors,
      formatFullName,
      cleanStr,
      formatPhoneNumber,
      handleExport,
      findPermission,
    };
  },
});
</script>

<style scoped lang="scss">
/* Channel badges */
.channel-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.25rem 0.625rem;
  border-radius: 9999px;
  font-size: 0.75rem;
  font-weight: 500;

  i {
    font-size: 0.75rem;
  }

  &--email {
    background-color: #eff6ff;
    color: #1d4ed8;
  }

  &--sms {
    background-color: #f0fdf4;
    color: #15803d;
  }

  &--whatsapp {
    background-color: #ecfdf5;
    color: #059669;
  }

  &--push {
    background-color: #fef3c7;
    color: #d97706;
  }
}

/* Type badges */
.type-badge {
  display: inline-flex;
  align-items: center;
  padding: 0.25rem 0.625rem;
  border-radius: 9999px;
  font-size: 0.75rem;
  font-weight: 500;

  &--subscribe {
    background-color: #dcfce7;
    color: #166534;
  }

  &--unsubscribe {
    background-color: #fef3c7;
    color: #92400e;
  }

  &--spam {
    background-color: #fee2e2;
    color: #991b1b;
  }

  &--hard-bounce {
    background-color: #fecaca;
    color: #b91c1c;
  }

  &--soft-bounce {
    background-color: #fed7aa;
    color: #c2410c;
  }
}

/* Date display */
.date-display {
  color: #64748b;
  font-size: 0.875rem;
}

/* Cell icons */
.cell-icon {
  font-size: 1rem;
  display: block;
  text-align: center;
}

.cell-icon--active {
  color: #94c840;
}

.cell-icon--inactive {
  color: #d1d5db;
}

.fullname-cell .contact-name--empty {
  font-weight: 400;
  font-style: italic;
  color: #9ca3af;
}
</style>
